import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { RecipeMenuUsage } from '../types';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import RecipeV2EmptyState from './RecipeV2EmptyState';
import { UserContext } from '../../../components/shared/useUser';
import { MenuDisplayStatusChip } from '../../Range/components/MenuDisplayStatusChip';
import { MenuDisplayStatus } from '../../../app/services/api/range';
import { formatDate } from '../../../utils';
import { useTranslation } from 'react-i18next';

const UsageTab = ({ recipeId }: { recipeId: number }) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const navigate = useNavigate();
  const { franchises, hasPermissionTo } = useContext(UserContext);
  const [usage, setUsage] = useState<RecipeMenuUsage[] | null>(null);
  const [error, setError] = useState(false);
  const canViewMenus = hasPermissionTo(['MenuRead']);

  useEffect(() => {
    let active = true;
    setUsage(null);
    setError(false);

    if (!canViewMenus) return () => { active = false; };

    api.getRecipeUsage(recipeId)
      .then(result => { if (active) setUsage(result); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [api, canViewMenus, recipeId]);

  if (!canViewMenus) return <Alert severity="info">{t('usage.noPermission', 'Menu permission is required to view recipe usage.')}</Alert>;
  if (error) return <Alert severity="error">{t('usage.error')}</Alert>;
  if (!usage) return <Typography color="text.secondary">Loading…</Typography>;

  const getFranchiseName = (franchiseId: number) => franchises.find(franchise => franchise.franchiseId === franchiseId)?.franchiseName
    ?? `Franchise ${franchiseId}`;
  const formatUsageDate = (value?: string | null) => value ? formatDate(new Date(value)) : '—';

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t('usage.menu', 'Menu')}</TableCell>
          <TableCell>{t('usage.rangeSection', 'Range / Section')}</TableCell>
          <TableCell>{t('usage.franchise', 'Franchise')}</TableCell>
          <TableCell>{t('statusLabel')}</TableCell>
          <TableCell>{t('build.effectiveFrom')}</TableCell>
          <TableCell>{t('build.effectiveTo')}</TableCell>
          <TableCell>{t('usage.storeScope', 'Store Scope')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {usage.length === 0 ? (
          <RecipeV2EmptyState colSpan={7}>{t('usage.empty')}</RecipeV2EmptyState>
        ) : usage.map(item => (
          <TableRow key={item.menuId}
            hover={canViewMenus}
            onClick={canViewMenus ? () => navigate(`/range/menus/${item.menuId}`) : undefined}
            sx={canViewMenus ? { cursor: 'pointer' } : undefined}>
            <TableCell>
              <Typography color={canViewMenus ? 'primary' : 'text.primary'}
                fontWeight={canViewMenus ? 600 : undefined}>
                {item.menuName}
              </Typography>
            </TableCell>
            <TableCell>{item.rangeNames.length > 0 ? item.rangeNames.join(', ') : '—'}</TableCell>
            <TableCell>{getFranchiseName(item.franchiseId)}</TableCell>
            <TableCell><MenuDisplayStatusChip status={item.status as MenuDisplayStatus} /></TableCell>
            <TableCell>{formatUsageDate(item.effectiveFrom)}</TableCell>
            <TableCell>{formatUsageDate(item.effectiveTo)}</TableCell>
            <TableCell>{t('usage.allStores', 'All Stores in franchise')}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default UsageTab;
